import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { filter, map } from 'rxjs';

import { PrivaComboboxDirective } from '@priva/components/combobox';
import {
    PrivaFilterSearchComponent,
    PrivaFilterSearchConfig,
    PrivaQueryableDataSource,
    PrivaQueryableDataSourceFactory,
} from '@priva/components/filter';

import { GreenHouseMap, Row } from 'app/shared/features/map-row-selector/models';
import { sortByName } from 'app/shared/utils/sort.utils';
import { defaultSearchPrivaQueryParamsSearch } from 'app/state/green-house-map/greenhouse-map.state';

import { MapFilterStore } from './map-filter.store';

@Component({
    selector: 'app-map-filter',
    templateUrl: './map-filter.component.html',
    styleUrl: './map-filter.component.scss',
    imports: [
        NgSelectModule,
        ReactiveFormsModule,
        PrivaComboboxDirective,
        PrivaFilterSearchComponent,
        AsyncPipe,
    ],
    providers: [MapFilterStore],
})
export class MapFilterComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private componentStore = inject(MapFilterStore);
    dataSourceFactory = inject(PrivaQueryableDataSourceFactory);

    formGroup = new FormGroup({
        map: new FormControl<GreenHouseMap | undefined>(undefined),
    });

    greenHouseMaps$ = this.componentStore.greenHouseMaps$.pipe(map((data) => sortByName(data)));

    readonly rowsDataSource: PrivaQueryableDataSource<Row>;

    ngOnInit(): void {
        this.formGroup.controls.map.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((map): map is GreenHouseMap => !!map),
            )
            .subscribe((map) => {
                this.componentStore.updateSelectedGreenHouseMap(map);
            });

        this.componentStore.selectedGreenHouseMap$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((greenHouseMap): boolean => !!greenHouseMap),
            )
            .subscribe((greenHouseMap) => {
                this.formGroup.controls.map.setValue(greenHouseMap);
            });

        // We want to get last search params from the global store and apply them to the filter.
        this.componentStore.searchParams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((search) => {
            this.rowsDataSource.addSearch(search ?? defaultSearchPrivaQueryParamsSearch);
        });
        // At the same time, we want to ensure that user input is also captured and saved to the store.
        // It will listen until the component is destroyed, and will update the store on every change.
        this.rowsDataSource.queryParams$.subscribe((params) => {
            this.componentStore.updateSearchQuery(params.search);
        });
    }

    filterSearchConfig: PrivaFilterSearchConfig = {
        type: 'search',
        id: 'searchRows',
        title: 'GENERAL.TITLE.SEARCH_LOCATION',
        placeholder: 'GENERAL.TITLE.SEARCH_LOCATION',
        options: ['name'],
        searchOptions: {
            matchType: 'single',
        },
    };

    constructor() {
        this.rowsDataSource = this.dataSourceFactory.create();
        this.rowsDataSource.setData(this.componentStore.mapRows$);
        this.rowsDataSource.results$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((filtered: Row[]) => {
                this.componentStore.updateFilteredRows(filtered);
            });
    }
}
